import React from 'react';

import { match } from '@/common/utils/match';
import type {
  AvailableCalculationScenarios,
  CompletedCalculationTactic,
  LoadFlowScenarioResults
} from '@/features/execute-calculation-set/overmind/calculation';
import type { LoadFlowSummary } from '@/features/execute-calculation-set/overmind/calculation/derived-state/loadFlowSummary';
import { useAppState } from '@/overmind';

import { isCalculationTacticResultUnavailable } from '../../overmind/calculation/actions';
import type { LvLoadFlowSummary } from '../../overmind/calculation/derived-state/loadFlowSummary/lowVoltage/LvLoadFlowSummary';
import type { LvLoadFlowSummaryEvaluatedResults } from '../../overmind/calculation/derived-state/loadFlowSummary/lowVoltage/LvLoadFlowSummaryEvaluatedResults';
import type { MvLoadFlowSummary } from '../../overmind/calculation/derived-state/loadFlowSummary/mediumVoltage/MvLoadFlowSummary';
import type { MvLoadFlowSummaryEvaluatedResults } from '../../overmind/calculation/derived-state/loadFlowSummary/mediumVoltage/MvLoadFlowSummaryEvaluatedResults';
import type { ShortCircuitSummary } from '../../overmind/calculation/derived-state/shortCircuitSummary';
import type { LvShortCircuitSummary } from '../../overmind/calculation/derived-state/shortCircuitSummary/lowVoltage/LvShortCircuitSummary';
import type { LvShortCircuitSummaryEvaluatedResults } from '../../overmind/calculation/derived-state/shortCircuitSummary/lowVoltage/LvShortCircuitSummaryEvaluatedResults';
import type { MvShortCircuitSummary } from '../../overmind/calculation/derived-state/shortCircuitSummary/mediumVoltage/MvShortCircuitSummary';
import type { MvShortCircuitSummaryEvaluatedResults } from '../../overmind/calculation/derived-state/shortCircuitSummary/mediumVoltage/MvShortCircuitSummaryEvaluatedResults';
import type { NonEmptyArray } from '../LowVoltageCalculation/LoadFlowSummaryCalculationResults/LvLoadFlowSummaryCalculationResults';
import { LvLoadFlowSummaryCalculationResults } from '../LowVoltageCalculation/LoadFlowSummaryCalculationResults/LvLoadFlowSummaryCalculationResults';
import { LvShortCircuitSummaryCalculationResults } from '../LowVoltageCalculation/ShortCircuitSummaryCalculationResults/LvShortCircuitSummaryCalculationResults';
import { MvLoadFlowSummaryCalculationResults } from '../MediumVoltageCalculation/LoadFlowSummaryCalculationResults';
import { MvShortCircuitSummaryCalculationResults } from '../MediumVoltageCalculation/ShortCircuitSummaryCalculationResults/MvShortCircuitSummaryCalculationResults';
import { CollapsibleCard } from './CollapsibleCard';

type CalculationsSummaryProps = {
  calculationTacticResult: CompletedCalculationTactic;
  loadFlowSummary: LoadFlowSummary;
  shortCircuitSummary: ShortCircuitSummary;
};
export function CalculationsSummary(props: CalculationsSummaryProps) {
  const availableCalculationScenarios =
    useAppState().calculation.availableCalculationScenarios;

  const calculationTactic = useAppState().calculation.calculationTactic.value;
  if (isCalculationTacticResultUnavailable(calculationTactic)) return null;
  const voltagelevel = calculationTactic?.calculationSetResult.voltageLevel;

  const loadFlowResults = props.calculationTacticResult.calculationSetResult
    .calculationScenarios
    .loadFlow as unknown as NonEmptyArray<LoadFlowScenarioResults>;

  return (
    <CollapsibleCard
      expandedByDefault
      header={'Summary'}
      content={
        <>
          {match.allOf(voltagelevel, {
            LowVoltage: _ =>
              createLvSummary(
                availableCalculationScenarios,
                (props.loadFlowSummary as LvLoadFlowSummary).value,
                loadFlowResults,
                (props.shortCircuitSummary as LvShortCircuitSummary).value
              ),
            MediumVoltage: _ =>
              createMvSummary(
                availableCalculationScenarios,
                (props.loadFlowSummary as MvLoadFlowSummary).value,
                loadFlowResults,
                (props.shortCircuitSummary as MvShortCircuitSummary).value
              )
          })}
        </>
      }
    />
  );
}

const createLvSummary = (
  availableCalculationScenarios: AvailableCalculationScenarios,
  loadFlowSummary: LvLoadFlowSummaryEvaluatedResults,
  loadFlowResults: NonEmptyArray<LoadFlowScenarioResults>,
  shortCircuitSummary: LvShortCircuitSummaryEvaluatedResults
) => {
  return (
    <>
      {loadFlowCalculationAvailable(availableCalculationScenarios) && (
        <LvLoadFlowSummaryCalculationResults
          loadFlowSummary={loadFlowSummary}
          loadFlowScenarios={loadFlowResults}
        />
      )}
      {shortCircuitCalculationAvailable(availableCalculationScenarios) && (
        <LvShortCircuitSummaryCalculationResults
          shortCircuitSummary={shortCircuitSummary}
        />
      )}
    </>
  );
};

const createMvSummary = (
  availableCalculationScenarios: AvailableCalculationScenarios,
  loadFlowSummary: MvLoadFlowSummaryEvaluatedResults,
  loadFlowResults: NonEmptyArray<LoadFlowScenarioResults>,
  shortCircuitSummary: MvShortCircuitSummaryEvaluatedResults
) => {
  return (
    <>
      {loadFlowCalculationAvailable(availableCalculationScenarios) && (
        <MvLoadFlowSummaryCalculationResults
          loadFlowSummary={loadFlowSummary}
          loadFlowScenarios={loadFlowResults}
        />
      )}
      {shortCircuitCalculationAvailable(availableCalculationScenarios) && (
        <MvShortCircuitSummaryCalculationResults
          shortCircuitSummary={shortCircuitSummary}
        />
      )}
    </>
  );
};

const loadFlowCalculationAvailable = (
  availableCalculationScenarios: AvailableCalculationScenarios
) => {
  return (
    (availableCalculationScenarios.type === 'SOME_AVAILABLE' &&
      availableCalculationScenarios.scenarios.has('LOAD_FLOW')) ||
    availableCalculationScenarios.type === 'ALL_AVAILABLE'
  );
};

const shortCircuitCalculationAvailable = (
  availableCalculationScenarios: AvailableCalculationScenarios
) => {
  return (
    (availableCalculationScenarios.type === 'SOME_AVAILABLE' &&
      availableCalculationScenarios.scenarios.has('SHORT_CIRCUIT')) ||
    availableCalculationScenarios.type === 'ALL_AVAILABLE'
  );
};
